// src/data/themes.ts
// ─────────────────────────────────────────────────────────────────────────────
// THEME CONFIGURATION
//
// Add a new theme:
//   1. Add a [data-theme="your-id"] block in src/themes.css with variable
//      overrides for all color/font/texture tokens.
//   2. Add an entry to the THEMES array below with start/end dates.
//
// Preview a theme without waiting for its date:
//   URL param:    /?theme=july4th
//   localStorage: localStorage.setItem('theme-override', 'july4th')
//   Reset:        localStorage.removeItem('theme-override')
// ─────────────────────────────────────────────────────────────────────────────

export interface ThemeDate {
  /** Calendar month, 1–12 */
  month: number;
  /** Day of month, 1–31 */
  day: number;
}

export interface ThemeDefinition {
  /** Unique ID — must match the [data-theme="..."] attribute in themes.css */
  id: string;
  /** Human-readable label for dev tooling */
  displayName: string;
  /**
   * Start of the auto-activation window (inclusive).
   * null = this theme is manual-only (only activated via override).
   */
  startDate: ThemeDate | null;
  /**
   * End of the auto-activation window (inclusive).
   * null = this theme is manual-only (only activated via override).
   */
  endDate: ThemeDate | null;
  /**
   * Optional: restrict auto-activation to a specific calendar year.
   * Use this for one-time events (e.g. America's 250th in 2026).
   * If omitted, the theme auto-activates every year its date range matches.
   */
  year?: number;
}

/**
 * Ordered list of themes. On each page load, ThemeContext walks this array and
 * activates the FIRST theme whose date range contains today's date.
 * The last entry (americana) has null dates and acts as the permanent fallback.
 *
 * Date ranges intentionally don't overlap. If two ranges overlap, the earlier
 * entry in this array wins.
 */
export const THEMES: ThemeDefinition[] = [
  // ── Seasonal themes ─────────────────────────────────────────────────────
  // Only the July 4th themes are kept; the other holiday themes (Valentine's,
  // Memorial Day, Labor Day, Halloween, Thanksgiving, Christmas, New Year's)
  // and the Hanukkah pennant were removed 2026-10-11 at the owner's request.
  // America250 must come before july4th — it takes priority in 2026
  {
    id: "america250",
    displayName: "America's 250th Birthday",
    startDate: { month: 6, day: 6 }, // 4 weeks before Jul 4
    endDate: { month: 7, day: 31 }, // run through all of July 2026
    year: 2026,
  },
  {
    id: "july4th",
    displayName: "Independence Day",
    startDate: { month: 6, day: 6 }, // 4 weeks before Jul 4
    endDate: { month: 7, day: 7 },
  },
  // ── Default fallback ─────────────────────────────────────────────────────
  // Must be last — null dates mean it never auto-matches a date range;
  // it is used only when no other theme is active.
  {
    id: "americana",
    displayName: "Americana (Default)",
    startDate: null,
    endDate: null,
  },
];
